import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import VirtualCareModal from '../virtualCareModal/virtualCareModal';
import ServiceAreaModal from '../serviceAreaModal/serviceAreaModal';
import './styles.css';
import pinImg from '../../images/pin.png';
import externalLinkImg from '../../images/external.png';
import virtualCareImg from '../../images/virtualCareIcon.svg';
import boardCertifiedImg from '../../images/star.png';
import handicapImg from '../../images/wheelchair.png';
import acceptingNewPatientsImg from '../../images/personOutline.svg';
import babyIcon from '../../images/babyIcon.svg';
import trainIcon from '../../images/train.svg';
import tick from '../../images/tick.png';
import InfoTooltip from '../infoTooltip/infoTooltip';
import ExternalLinkModal from '../externalLinkModal/externalLinkModal';

// Format phone number to 123-456-7890 format
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  // Format as XXX-XXX-XXXX
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  // Return original if not 10 digits
  return phone;
};

// Some API responses send the literal string "null"/"Null"/"NULL" (or an empty/
// whitespace-only string) instead of an actual JS null/undefined for missing URL
// fields — a plain `url && url !== null` check doesn't catch those string variants, so
// the website link would render (and try to navigate to "https://null") instead of
// falling back to "No website found". Normalize and reject all of null/undefined/''/
// whitespace-only/"null" in any casing.
const isValidUrl = (url) => {
  if (url === null || url === undefined) return false;
  const trimmed = String(url).trim();
  if (trimmed === '') return false;
  if (trimmed.toLowerCase() === 'null') return false;
  return true;
};

const ProviderSummary = ({ provider, fieldConfig, address, sanitizedPhone, specialty, boardCertified, providerNameId, providerDetailsType }) => {
  const { isProviderCardFieldEnabled, isProviderDetailsFieldEnabled } = usePlanFeatures();
  const [showVirtualCareModal, setShowVirtualCareModal] = useState(false);
  const [showServiceAreaModal, setShowServiceAreaModal] = useState(false);
  const virtualCareTriggerRef = useRef(null);
  // "You are now leaving Aetna Better Health®" confirmation for the email link below —
  // same pattern used for external links elsewhere (browseBy.jsx, providerDetailsType2.jsx,
  // etc.). mailto: doesn't actually navigate away from the site the way an http(s) link
  // does, but per request this should still show the same confirmation before handing off
  // to the user's mail client, for consistency with how every other outbound link on this
  // card behaves.
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [pendingEmailAddress, setPendingEmailAddress] = useState(null);
  // Same "you are now leaving" confirmation for the website link — the copy doc
  // (reports/Provider search 2.0_RESULTS screens.docx) calls for the website field to be
  // "linked and violet with an exit modal" and this card's website link previously opened
  // directly with no confirmation, unlike every other outbound link on this card.
  const [websiteModalOpen, setWebsiteModalOpen] = useState(false);
  const [pendingWebsiteUrl, setPendingWebsiteUrl] = useState(null);

  if (!provider) return null;

  const providerUrl = provider.providerUrl || '';
  // `provider.providerHandicap` doesn't exist anywhere in the API response — handicap
  // accessibility is only ever reported per-location under `location.ADA.handicapAccess`
  // ('Y'/'N') or the descriptive `location.ADA.handicapAccessibility` string (same fields
  // the "Some locations" service-location detail and handicapAccessibilityModal.jsx read
  // from). Reading the nonexistent top-level field meant this card-level badge always
  // fell back to "Not handicap accessible" even when a location clearly had it — matching
  // the reported defect. Mirror the same "any location" check already used for
  // hasPublicTransit/hasVirtualCare below instead.
  // "None Specified" (and similar empty-ish sentinel strings the API sends, e.g.
  // "None", "N/A", "Not Specified") is NOT an accessibility value — it explicitly means
  // no ADA accessibility info was provided for that location. Treating any non-empty
  // string as "accessible" caused providers whose only ADA data was
  // handicapAccess: "N" / handicapAccessibility: "None Specified" to incorrectly show up
  // as "Handicap accessible" in the summary card.
  const NON_ACCESSIBLE_SENTINELS = new Set(['none specified', 'none', 'not specified', 'n/a', 'na']);
  const isLocationHandicapAccessible = (location) => {
    const ada = location.ADA || {};
    if (String(ada.handicapAccess || '').toUpperCase() === 'Y') return true;
    if (String(ada.handicapAccess || '').toUpperCase() === 'N') return false;
    const accessibility = typeof ada.handicapAccessibility === 'string' ? ada.handicapAccessibility.trim() : '';
    return accessibility.length > 0 && !NON_ACCESSIBLE_SENTINELS.has(accessibility.toLowerCase());
  };
  const providerHandicap = provider.locations && provider.locations.some(isLocationHandicapAccessible);
  // The "Some locations" subtext should only appear when accessibility is inconsistent
  // across locations (mirrors the "hasMixedNewAssigns" treatment used for "Accepting new
  // patients" above) — previously it was shown any time ANY location had it, even when
  // EVERY location was accessible, incorrectly implying only some locations qualified.
  const hasMixedHandicapAccess = provider.locations &&
    provider.locations.length > 1 &&
    providerHandicap &&
    !provider.locations.every(isLocationHandicapAccessible);
  const providerVirtualCare = provider.providerVirtualCare || '';

  const handleOpenVirtualCareModal = () => setShowVirtualCareModal(true);
  const handleCloseVirtualCareModal = () => {
    setShowVirtualCareModal(false);
    setTimeout(() => virtualCareTriggerRef.current?.focus(), 0);
  };

  const handleOpenServiceAreaModal = () => setShowServiceAreaModal(true);
  const handleCloseServiceAreaModal = () => setShowServiceAreaModal(false);

  const handleEmailLinkClick = (e) => {
    e.preventDefault();
    setPendingEmailAddress(provider.email);
    setEmailModalOpen(true);
  };

  const handleEmailModalConfirm = () => {
    if (pendingEmailAddress) {
      window.open(`mailto:${pendingEmailAddress}`, '_blank', 'noopener,noreferrer');
    }
    setEmailModalOpen(false);
    setPendingEmailAddress(null);
  };

  const handleEmailModalCancel = () => {
    setEmailModalOpen(false);
    setPendingEmailAddress(null);
  };

  const handleWebsiteLinkClick = (e, url) => {
    e.preventDefault();
    setPendingWebsiteUrl(url);
    setWebsiteModalOpen(true);
  };

  const handleWebsiteModalConfirm = () => {
    if (pendingWebsiteUrl) {
      window.open(pendingWebsiteUrl, '_blank', 'noopener,noreferrer');
    }
    setWebsiteModalOpen(false);
    setPendingWebsiteUrl(null);
  };

  const handleWebsiteModalCancel = () => {
    setWebsiteModalOpen(false);
    setPendingWebsiteUrl(null);
  };

  const isProviderHospital = () => {
    // Use providerDetailsType prop (2 = Hospital/Facility) or fallback to provider.providerType
    return providerDetailsType === 2 || provider.providerType === "Hospital";
  }
  
  // Check if provider is accredited (for hospitals/institutions)
  const isAccredited = provider.accreditations && provider.accreditations.length > 0;
  
  // Check accepting new patients status across all locations
  const acceptingNewPatientsStatus = (() => {
    if (!provider.locations || provider.locations.length === 0) {
      return 'none'; // No locations
    }

    const acceptingCount = provider.locations.filter(
      l => l.newAssigns === "-1" || l.newAssigns === -1
    ).length;

    if (acceptingCount === provider.locations.length) {
      return 'all'; // All locations accepting
    } else if (acceptingCount === 0) {
      return 'none'; // No locations accepting
    } else {
      return 'some'; // Some locations accepting
    }
  })();

  // Legacy variable for compatibility (checking if accepting)
  const isAcceptingNewPatients = acceptingNewPatientsStatus === 'all';

  // Show "Some locations" only when locations have mixed newAssigns values (not all the same)
  const hasMixedNewAssigns = provider.locations &&
    provider.locations.length > 1 &&
    new Set(provider.locations.map(l => String(l.newAssigns ?? ''))).size > 1;
  
  // Check if any location offers telehealth/virtual care
  const isLocationVirtualCare = (location) => {
    const telehealth = location.telehealth || location.telemedicine || '';
    return String(telehealth).trim() !== '';
  };
  const hasVirtualCare = provider.locations && provider.locations.some(isLocationVirtualCare);
  // "Some locations" only when virtual care is inconsistent across locations — mirrors
  // hasMixedHandicapAccess above.
  const hasMixedVirtualCare = provider.locations &&
    provider.locations.length > 1 &&
    hasVirtualCare &&
    !provider.locations.every(isLocationVirtualCare);

  // Check if any location has easy access to public transit
  const isLocationPublicTransitAccessible = (location) => {
    const pubTrans = String(location.ADA?.pubTrans || '').toUpperCase();
    return pubTrans === 'YES' || pubTrans === 'Y';
  };
  const hasPublicTransit = provider.locations && provider.locations.some(isLocationPublicTransitAccessible);
  // "Some locations" should only show when access is inconsistent across locations —
  // i.e. at least one location has it ("Yes"/"Y") but at least one other doesn't
  // ("", "No", "N") — mirroring hasMixedHandicapAccess above. When EVERY location
  // consistently has it (or consistently doesn't), no "Some locations"/"All locations"
  // subtext distinction is needed there — but per request, show "All locations" in
  // that case instead.
  const hasMixedPublicTransit = provider.locations &&
    provider.locations.length > 1 &&
    hasPublicTransit &&
    !provider.locations.every(isLocationPublicTransitAccessible);

  // Check if any location accepts Medicaid — mirrors isLocationPublicTransitAccessible
  // above; `acceptsMedicaid` is reported per-location as a 'Y'/'N' (or 'Yes'/'No') string.
  const acceptsMedicaid = provider.acceptsMedicaid && provider.acceptsMedicaid === ('Y' || 'Yes') ;
  
  // Accepting new patients badge markup — reused twice below: once in its
  // normal desktop position (floated right of the card, at the very end of
  // the DOM), and once in a mobile-only position rendered immediately after
  // the name/specialty header (per the requested mobile element order:
  // name+specialty -> accepting new patients -> address -> ...). This is now the ONLY
  // rendered copy at every breakpoint — see A11y fix note below on
  // .accepting-new-patients-wrapper for why the previous "two copies, CSS toggles which
  // is visible" approach was wrong.
  const acceptingNewPatientsEnabled = isProviderCardFieldEnabled('acceptingNewPatients') || isProviderCardFieldEnabled('newAssigns');
  const acceptingNewPatientsBadge = acceptingNewPatientsEnabled && (
    <div className="div-9">
      <img className="img" src={acceptingNewPatientsImg} alt="" />
      <p className="accepting-new">
        <span className="text-wrapper-8">
          {acceptingNewPatientsStatus === 'all' && 'Accepting new patients'}
          {acceptingNewPatientsStatus === 'none' && 'Not accepting new patients'}
          {acceptingNewPatientsStatus === 'some' && 'Accepting new patients'}
        </span>
        <br />
        {acceptingNewPatientsStatus === 'some' && <span className="text-wrapper-6">Some locations</span>}
      </p>
    </div>
  );

return (
  // new code starts here ...
  <>
  <div className="frame">
    <div className="group-wrapper">
      <div className="group">
        <div className="div">
          {/* name-badge-row: wraps the name/specialty block and the "Accepting new
              patients" badge in a real flex row so the badge is vertically CENTERED
              against the name+specialty block's actual height via align-items: center,
              instead of the previous approach (position: absolute; top: 0 on
              .accepting-new-patients-wrapper), which pinned the badge to the exact top
              edge of .div regardless of how tall the name/specialty block was —
              reported as the badge looking "constrained"/stuck at the top instead of
              centered. Flexbox's align-items: center naturally handles this regardless
              of name length (1 vs 2 lines) or badge content (1 vs 2 lines for the
              "Some locations" subtext case), with no fragile hardcoded offsets. */}
          <div className="name-badge-row">
            <div className="group-2">
              <h3 id={providerNameId} className="text-wrapper" translate='no'>
                {provider?.firstName || provider?.lastName 
                  ? `${provider?.firstName || ''} ${provider?.lastName || ''}${isProviderCardFieldEnabled('providerDesignation') && provider?.providerDesignation ? `, ${provider.providerDesignation}` : ''}`
                  : 'No name found'}
              </h3>
              <div className="text-wrapper-2">{provider?.primarySpecialty?.[0]?.primarySpecialty || ''}</div>
            </div>
            {/* A11y fix: this used to be a MOBILE-ONLY duplicate (a second, visually-hidden-
                on-desktop copy of acceptingNewPatientsBadge existed at the very end of the
                DOM below, floated top-right via CSS for desktop). Screen readers read DOM
                order, not visual/flex position — so on desktop, AT users had to tab/read
                through the ENTIRE card (address, phone, website, virtual care, board
                certified, handicap, etc.) before ever reaching "Not accepting new patients",
                even though it visually appears right beside the name at the very top of the
                card. QA (8/5) confirmed this violates expected reading order (should come
                right after name+specialty, before the rest of the details). Fixed by making
                THIS single instance — already correctly positioned right after name+specialty
                in the DOM — the only one rendered at any breakpoint. styles.css now lays it
                out as a flex row sibling of .group-2 for desktop (see .name-badge-row) while
                leaving it as normal in-flow content for mobile (unchanged from before). */}
            {acceptingNewPatientsBadge && (
              <div className="accepting-new-patients-wrapper">
                {acceptingNewPatientsBadge}
              </div>
            )}
          </div>
          <div className="div-2">
            <div className="molecules-category">
              <div className="div-3">
                {(isProviderCardFieldEnabled('address') || isProviderCardFieldEnabled('distance')) && (
                  <div className="first">
                    <div className="div-4">
                      <div className="div-5">
                        {isProviderCardFieldEnabled('distance') && (
                          <p className="div-6">
                            {provider.distanceInMiles !== null && provider.distanceInMiles !== 0 ? (
                              <>
                                <img className="img" src={pinImg} alt="" />
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${provider.locations[0]?.provPhyAddr1},+${provider.locations[0]?.provPhyCity},+${provider.locations[0]?.provPhyState}+${provider.locations[0]?.provPhyZip}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <span className="span">{provider.distanceInMiles} miles from you</span>
                                </a>
                              </>
                            ) : (
                              <>
                                <img className="img" src={pinImg} alt="" />
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${provider.locations[0]?.provPhyAddr1},+${provider.locations[0]?.provPhyCity},+${provider.locations[0]?.provPhyState}+${provider.locations[0]?.provPhyZip}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <span className="span">0 miles from you</span>
                                </a>
                              </>
                            )}
                          </p>
                        )} 
                        {isProviderCardFieldEnabled('address') && (
                          <p className="element-lee-street" translate='no'>
                            {(provider.locations[0]?.provPhyAddr1 || provider.locations[0]?.provPhyCity || provider.locations[0]?.provPhyState || provider.locations[0]?.provPhyZip) && (
                              <>
                                {provider.locations[0]?.provPhyAddr1 && (
                                  <span className="text-wrapper-3">{provider.locations[0].provPhyAddr1}<br /></span>
                                )}
                                 {provider.locations[0]?.provPhyAddr2 && (
                                  <span className="text-wrapper-3">{provider.locations[0].provPhyAddr2}<br /></span>
                                )}
                                <span className="text-wrapper-3">
                                  {[
                                    provider.locations[0]?.provPhyCity,
                                    provider.locations[0]?.provPhyState,
                                    provider.locations[0]?.provPhyZip
                                  ].filter(Boolean).join(', ')}
                                </span>
                                {provider.locations[0]?.provPhyCounty && (
                                  <>
                                    <br />
                                    <span className="text-wrapper-3">{provider.locations[0].provPhyCounty} County</span>
                                  </>
                                )}
                              </>
                            ) 
                            // : (
                            //   <span className="text-wrapper-3">No address found</span>
                            // )
                            }
                            {providerDetailsType !== 4 && provider.locations?.length > 1 && (
                              <>
                                <br />
                                <span className="text-wrapper-4">Multiple locations</span>
                              </>
                            )}
                            {providerDetailsType === 4 && (
                              <>
                                <br />
                                <button onClick={handleOpenServiceAreaModal} className="check-service-area-link" aria-label="Check service area">
                                  Check service area
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '10px' }}>
                                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="contact-info-column">
                  {isProviderCardFieldEnabled('phone') && (
                    <div className="second">
                      <div className="div-4">
                        <div className="div-5">
                          <div className="phone-wrapper">
                            <p className="phone-label-inline">Phone: </p>
                            {provider.locations[0]?.phone && provider.locations[0]?.phone !== ('null' || '') ? (
                              <a href={`tel:${provider.locations[0]?.phone}`} target="_blank" rel="noopener noreferrer" translate='no'>
                                <div className="div-6" id="phoneMain">{formatPhoneNumber(provider.locations[0]?.phone)}</div>
                              </a>
                            ) : (
                              <div className="div-6">No phone found</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Fax — new field, must appear directly below Phone and above
                      Website per the design/AC. Fails CLOSED (defaultValue: false)
                      rather than the usual fail-open default used elsewhere in this
                      file: this is a brand-new field being introduced for a single
                      plan's rollout, and per the ticket must NOT appear on any other
                      existing plan's provider card until that plan's plan-features
                      response explicitly turns on the flag. NOTE: the API returns
                      this flag under the `providerDetails` category (not
                      `providerCard`) — e.g. `featureFlags.providerDetails.fax` — so
                      this must read via isProviderDetailsFieldEnabled, not
                      isProviderCardFieldEnabled, or a plan turning this on would
                      silently never actually enable it here. */}
                  {isProviderDetailsFieldEnabled('fax') && (
                    <div className="second">
                      <div className="div-4">
                        <div className="div-5">
                          <div className="phone-wrapper">
                            <p className="phone-label-inline">Fax: </p>
                            {provider.locations[0]?.fax ? (
                              <div className="div-6" id="faxMain">{formatPhoneNumber(provider.locations[0]?.fax)}</div>
                            ) : (
                              <div className="div-6">None found</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {isProviderCardFieldEnabled('email', false) && provider?.email && (
                    <div className="div-8">
                      <div className="text-link">
                        <a
                          href={`mailto:${provider.email}`}
                          onClick={handleEmailLinkClick}
                          className="email-link"
                        >
                          <span className="lorem-ipsum-dolor email-text">{provider.email}</span>
                        </a>
                      </div>
                    </div>
                  )}
                  {isProviderCardFieldEnabled('website') && (
                    <div className="div-8">
                      <div className="text-link">
                        <div className="lorem-ipsum-dolor">
                          {isValidUrl(provider?.locations[0]?.url) ? (
                            <a
                              href={/^https?:\/\//i.test(provider?.locations[0].url) ? provider?.locations[0].url : `https://${provider?.locations[0].url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="website-url-link"
                              onClick={(e) => handleWebsiteLinkClick(e, /^https?:\/\//i.test(provider?.locations[0].url) ? provider?.locations[0].url : `https://${provider?.locations[0].url}`)}
                            >
                              <span className="website-url-text">Website</span>
                              <img className="external-link" src={externalLinkImg} alt="" />
                            </a>
                          ) : (
                            <span className="no-website-text">No website found</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {(isProviderCardFieldEnabled('virtualCareAvailable') || isProviderCardFieldEnabled('telemedicine')) && (
                    <div className="virtual-care-wrapper">
                      <div className="div-4">
                        <div className="div-5">
                          <p className="p" style={{ marginBottom: 0 }}>
                            <span className="text-wrapper-5">
                              <img className="img" src={virtualCareImg} alt="" />
                              {hasVirtualCare ? 'Virtual care' : 'No virtual care'}
                            </span>
                            <InfoTooltip
                              heading="Virtual care"
                              body="Telehealth or telemedicine. Use a phone or computer to connect with your provider. This works when you don't need to see them in person."
                            />
                            <br />
                            {hasVirtualCare ? (
                              hasMixedVirtualCare && <span className="text-wrapper-6" style={{ marginLeft: '32px' }}>Some locations</span>
                            ) : (
                              <span className="text-wrapper-4" style={{ marginLeft: '32px' }}>Office visits only</span>
                            )}
                          </p>
                        </div>
                      </div>
                      {/* Fail CLOSED (defaultValue: false) unlike every other
                          isProviderCardFieldEnabled(...) call in this file. This is a
                          newer, opt-in-by-plan feature — unlike the rest of the fields
                          here, a plan whose API response doesn't define
                          providerCard.virtualCareModal at all has never been configured
                          to show this link and shouldn't see it appear by default (was
                          incorrectly showing for plan PA, which has no such key in its
                          plan-features response, under the standard fail-open default). */}
                      {hasVirtualCare && isProviderCardFieldEnabled('virtualCareModal', false) && (
                        <a
                          ref={virtualCareTriggerRef}
                          href="#"
                          className="check-virtual-care-btn"
                          onClick={(e) => { e.preventDefault(); handleOpenVirtualCareModal(); }}
                        >
                          Check virtual care options
                        </a>
                      )}
                    </div>
                  )}
                  {isProviderCardFieldEnabled('birthFriendly') && provider.birthFriendly && (
                    <div className="birth-friendly-row">
                      <img className="img" src={babyIcon} alt="" />
                      <span className="text-wrapper-5">Birthing-friendly care</span>
                      <InfoTooltip
                        heading="Birth-friendly care"
                        body="This is a hospital or health system that tries to improve care before and after birth. They use safety methods that work. And they focus on health equity. The goal is to care for people from all racial and ethnic groups."
                      />
                    </div>
                  )}
                </div>
                <div className="badges-column">

                  {isProviderCardFieldEnabled('acceptsMedicaid') && acceptsMedicaid && (
                    <div className="div-4">
                      <div className="div-5">
                        <p className="p">
                          <span className="text-wrapper-5">
                            <img className="img" src={tick} alt="" />
                            {acceptsMedicaid ? 'Accepts Medicaid' : 'Does not accept Medicaid'}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                  {isProviderHospital() ? (isProviderCardFieldEnabled('boardCertified')) ? (
                    <div className="div-4">
                      <img className="img" src={boardCertifiedImg} alt="" />
                      <div className="div-5">
                        <div className="text-wrapper-7">{isAccredited ? 'Accredited' : 'No accreditation found'}</div>
                        <InfoTooltip
                          heading="Accreditation"
                          body="<span>Expert health groups have checked this location. They say it meets quality standards for patient care and safety. You can find out if certain groups <a href='https://www.jointcommission.org/en/about-us/recognizing-excellence/find-accredited-international-organizations' target='_blank' rel='noopener noreferrer'>accredit a location</a>.</span>"
                        />
                      </div>
                    </div>
                  ) : <></>  :  (isProviderCardFieldEnabled('boardCertified') && providerDetailsType === 1) ? (
                    <div className="div-4">
                      <img className="img" src={boardCertifiedImg} alt="" />
                      <div className="div-5">
                        <div className="text-wrapper-7">{boardCertified ? 'Board certified' : 'No board certification found'}</div>
                        <InfoTooltip
                          heading="Board certification"
                          body="<span>This provider passed special tests in their field of health care. You can find out if they are <a href='https://www.certificationmatters.org/find-my-doctor/' target='_blank' rel='noopener noreferrer'>board-certified</a> in a specialty. Learn more about <a href='https://www.certificationmatters.org/' target='_blank' rel='noopener noreferrer'>board certification</a>.</span>"
                        />
                      </div>
                    </div>
                  ) : <></>}
                  {isProviderCardFieldEnabled('handicapAccessible') && (
                    <div className="div-4">
                      <div className="div-5">
                        <p className="p">
                          <span className="text-wrapper-5">
                            <img className="img" src={handicapImg} alt="" />
                            {providerHandicap ? 'Handicap accessible' : 'Not handicap accessible'}
                          </span>
                          <br />
                          {hasMixedHandicapAccess && <span className="text-wrapper-6" style={{ marginLeft: '32px' }}>Some locations</span>}
                        </p>
                      </div>
                    </div>
                  )}
                  {isProviderCardFieldEnabled('publicTransportation') && (
                    <div className="div-4">
                      <div className="div-5">
                        <p className="p">
                          <span className="text-wrapper-5">
                            <img className="img" src={trainIcon} alt="" />
                            {hasPublicTransit ? 'Easy access to public transportation' : 'No easy access to public transportation'}
                          </span>
                          <br />
                          <span className="text-wrapper-6" style={{ marginLeft: '32px' }}>{hasMixedPublicTransit ? 'Some locations' : ''}</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  {/* Portaled to document.body — this card renders inside .results-list, which has
      `transform: translateZ(0)` (see results/styles.css) to work around an Edge/
      Chromium compositor bug. A CSS transform on an ancestor creates a new containing
      block for `position: fixed` descendants, so without a portal this modal's fixed
      overlay was positioned relative to .results-list instead of the viewport —
      rendering it off-position/invisible (and/or clipped by an ancestor's overflow)
      while its body-scroll-lock and focus-trap effects still fully engaged, so the
      page appeared to "freeze" (scrolled, focus trapped, rest of page uninteractable)
      with no visible modal or overlay to interact with or escape other than pressing
      Escape. Matches the same portal pattern already used below for
      emailModalOpen/websiteModalOpen's ExternalLinkModal. */}
  {showVirtualCareModal && createPortal(
    <VirtualCareModal
      isOpen={showVirtualCareModal}
      onClose={handleCloseVirtualCareModal}
      provider={provider}
    />,
    document.body
  )}
  {showServiceAreaModal && createPortal(
    <ServiceAreaModal
      isOpen={showServiceAreaModal}
      onClose={handleCloseServiceAreaModal}
      provider={provider}
    />,
    document.body
  )}
  {emailModalOpen && createPortal(
    // Portaled to document.body — this card renders inside .results-list, which has
    // `transform: translateZ(0)` (see results/styles.css) to work around an Edge/
    // Chromium compositor bug. A CSS transform on an ancestor creates a new containing
    // block for `position: fixed` descendants, so without a portal this modal's fixed
    // overlay would be positioned relative to .results-list instead of the viewport —
    // rendering it off-position/invisible while its body-scroll-lock and focus-trap
    // effects still engaged, appearing to "freeze" the page with no visible modal to
    // interact with or escape. Matches the same portal pattern already used for this
    // modal in hoverTooltip.jsx/infoTooltip.jsx/providerDetailsType2.jsx.
    <ExternalLinkModal
      isOpen={emailModalOpen}
      onConfirm={handleEmailModalConfirm}
      onCancel={handleEmailModalCancel}
      url={pendingEmailAddress ? `mailto:${pendingEmailAddress}` : ''}
    />,
    document.body
  )}
  {websiteModalOpen && createPortal(
    <ExternalLinkModal
      isOpen={websiteModalOpen}
      onConfirm={handleWebsiteModalConfirm}
      onCancel={handleWebsiteModalCancel}
      url={pendingWebsiteUrl || ''}
    />,
    document.body
  )}
  </>
);
};

export default ProviderSummary;
